import { useState } from "react";
import { toast } from "sonner";
import { Check, ShieldCheck, Upload, Loader2, FileText, Phone } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { api, formatApiErrorDetail, trackEvent } from "../lib/api";
import { COMPANY, TYPES_DEMANDE, IMAGES } from "../lib/data";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BENEFITS = [
  "Étude de votre projet 100% gratuite",
  "Réponse rapide par un artisan local",
  "Conseils clairs, sans jargon",
  "Aucun engagement de votre part",
];

export default function Devis() {
  const [form, setForm] = useState({
    nom: "", telephone: "", email: "", code_postal: "",
    type_demande: "", type_bien: "", delai: "", message: "", rappel: false,
  });
  const [photo, setPhoto] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("La photo ne doit pas dépasser 3 Mo.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Merci d'accepter la politique de confidentialité."); return; }
    if (!form.type_demande) { toast.error("Merci de sélectionner un type de demande."); return; }
    setLoading(true);
    try {
      await api.post("/quote", { ...form, photo, consentement: consent });
      trackEvent("form_submit", `Devis envoyé - ${form.type_demande}`);
      setDone(true);
      toast.success("Votre demande a bien été envoyée !");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center">
        <Seo title="Demande envoyée — Carnier Toiture et Habitat" />
        <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-vegetal-100 text-vegetal-600">
          <Check className="h-8 w-8" />
        </div>
        <h1 className="mt-6 font-heading text-3xl font-bold text-neutral-900">Merci, votre demande est envoyée !</h1>
        <p className="mt-4 text-neutral-700">Un artisan de <strong>{COMPANY.name}</strong> vous recontacte au plus vite pour étudier votre projet. Pour toute urgence, appelez le <a href={`tel:${COMPANY.phoneRaw}`} className="text-terracotta-600 font-medium">{COMPANY.phone}</a>.</p>
      </div>
    );
  }

  return (
    <div>
      <Seo
        title="Demande de devis gratuit — Toiture & habitat | Carnier (Oise)"
        description="Demandez votre devis gratuit et sans engagement pour la rénovation de toiture, le ravalement, les gouttières ou l'isolation dans l'Oise, l'Île-de-France et la Picardie."
      />

      <section className="bg-vegetal-800 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-16">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-300">Devis gratuit</span>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-balance">Recevez votre devis gratuit</h1>
            <p className="mt-4 max-w-2xl text-vegetal-100/80 text-lg">Décrivez votre projet en quelques minutes. C'est simple, rapide et sans engagement.</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-20 grid lg:grid-cols-[1fr_360px] gap-10">
        <Reveal>
          <form onSubmit={submit} data-testid="devis-form" className="rounded-2xl bg-white border border-neutral-200 shadow-sm p-6 md:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Nom complet" required>
                <Input data-testid="devis-nom" required value={form.nom} onChange={set("nom")} placeholder="Jean Dupont" />
              </Field>
              <Field label="Téléphone" required>
                <Input data-testid="devis-telephone" required type="tel" value={form.telephone} onChange={set("telephone")} placeholder="06 12 34 56 78" />
              </Field>
              <Field label="Email" required>
                <Input data-testid="devis-email" required type="email" value={form.email} onChange={set("email")} placeholder="vous@email.fr" />
              </Field>
              <Field label="Code postal" required>
                <Input data-testid="devis-cp" required value={form.code_postal} onChange={set("code_postal")} placeholder="60000" />
              </Field>
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Type de demande" required>
                <Select value={form.type_demande} onValueChange={set("type_demande")}>
                  <SelectTrigger data-testid="devis-type-demande"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {TYPES_DEMANDE.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Type de bien">
                <Select value={form.type_bien} onValueChange={set("type_bien")}>
                  <SelectTrigger data-testid="devis-type-bien"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {["Maison individuelle", "Appartement", "Local professionnel", "Autre"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Délai souhaité">
                <Select value={form.delai} onValueChange={set("delai")}>
                  <SelectTrigger data-testid="devis-delai"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {["Dès que possible", "Sous 1 mois", "Sous 3 mois", "Je me renseigne"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Votre message" required>
              <Textarea data-testid="devis-message" required rows={5} value={form.message} onChange={set("message")} placeholder="Décrivez votre projet : type de toiture, surface, problème constaté (mousse, fuite, façade abîmée...)" />
            </Field>

            <div>
              <Label className="text-sm font-medium text-neutral-800">Photo du chantier (optionnel)</Label>
              <label className="mt-2 flex items-center gap-3 rounded-lg border border-dashed border-neutral-300 bg-sand-100 px-4 py-3 cursor-pointer hover:border-terracotta-400 transition-colors">
                <Upload className="h-5 w-5 text-neutral-400" />
                <span className="text-sm text-neutral-600">{photo ? "Photo ajoutée ✓ — cliquez pour changer" : "Ajouter une photo (JPG/PNG, max 3 Mo)"}</span>
                <input data-testid="devis-photo" type="file" accept="image/*" className="hidden" onChange={onPhoto} />
              </label>
            </div>

            <label className="flex items-start gap-3 rounded-lg bg-vegetal-50 border border-vegetal-100 p-4 cursor-pointer">
              <Checkbox data-testid="devis-rappel" checked={form.rappel} onCheckedChange={(v) => setForm((f) => ({ ...f, rappel: !!v }))} className="mt-0.5" />
              <span className="text-sm text-vegetal-800">Je préfère être rappelé(e) par téléphone pour discuter de mon projet.</span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="devis-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
              <span className="text-xs text-neutral-600 leading-relaxed">
                J'accepte que mes informations soient utilisées uniquement pour être recontacté(e) au sujet de ma demande. Elles ne sont ni cédées ni vendues. Voir notre{" "}
                <a href="/politique-confidentialite" className="text-terracotta-600 underline">politique de confidentialité</a>.
              </span>
            </label>

            <button data-testid="devis-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-terracotta-500 text-white hover:bg-terracotta-600 transition-colors font-medium shadow-sm disabled:opacity-60">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi en cours...</> : <><FileText className="h-4 w-4" /> Envoyer ma demande de devis</>}
            </button>
          </form>
        </Reveal>

        <Reveal delay={120}>
          <aside className="space-y-6 lg:sticky lg:top-24 self-start">
            <div className="rounded-2xl overflow-hidden shadow-sm ring-1 ring-black/5">
              <img src={IMAGES.facadeAfter} alt="Maison rénovée" className="w-full h-40 object-cover" />
            </div>
            <div className="rounded-2xl bg-white border border-neutral-200 shadow-sm p-6">
              <h3 className="font-heading font-semibold text-neutral-900 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-vegetal-600" /> Pourquoi demander un devis ?</h3>
              <ul className="mt-4 space-y-3">
                {BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-neutral-700">
                    <Check className="h-4 w-4 text-vegetal-600 mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
            <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-3 rounded-2xl bg-vegetal-600 text-white p-5 hover:bg-vegetal-700 transition-colors">
              <Phone className="h-6 w-6" />
              <div>
                <p className="text-xs text-vegetal-100">Préférez-vous appeler ?</p>
                <p className="font-heading font-bold text-lg">{COMPANY.phone}</p>
              </div>
            </a>
          </aside>
        </Reveal>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-sm font-medium text-neutral-800">{label} {required && <span className="text-terracotta-500">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
